import { flatten } from "lodash";
import {
  BINAURAL_DOME_SUBSTEP,
  BINAURAL_EARMOLD_SUBSTEP,
  BINAURAL_HEARING_AID_SUBSTEP,
  BINAURAL_RECEIVER_SUBSTEP,
  BINAURAL_TUBE_SUBSTEP,
  CONFIGURATION_STEP,
  LEFT_DOME_SUBSTEP,
  LEFT_EARMOLD_SUBSTEP,
  LEFT_HEARING_AID_SUBSTEP,
  LEFT_RECEIVER_SUBSTEP,
  LEFT_TUBE_SUBSTEP,
  RIGHT_DOME_SUBSTEP,
  RIGHT_EARMOLD_SUBSTEP,
  RIGHT_HEARING_AID_SUBSTEP,
  RIGHT_RECEIVER_SUBSTEP,
  RIGHT_TUBE_SUBSTEP,
} from "../../constants";
import { EarHearingAidValues } from "../../steps/hearing-aids-step/substeps/hearing-aids/hearing-aid-values.type";
import { EarReceiverValues } from "../../steps/hearing-aids-step/substeps/receivers/receiver-values.type";
import { EarTubeValues } from "../../steps/hearing-aids-step/substeps/tubes/tube-values.type";
import {
  D_OR_E_CONFIGURATION_DOMES,
  D_OR_E_CONFIGURATION_EARMOLDS,
  EARS_CONFIGURATION_BOTH,
  EARS_CONFIGURATION_LEFT,
  EARS_CONFIGURATION_RIGHT,
  OrderConfigurationStepValues,
  R_OR_T_CONFIGURATION_RECEIVERS,
  R_OR_T_CONFIGURATION_TUBES,
} from "../../steps/order-configuration-step/order-configuration-step-values.type";
import { StepData, StepId } from "../hearing-aids-wizard.type";
import { EarEarmoldValues } from "../../steps/hearing-aids-step/substeps/earmolds/earmold-values.type";
import { EarDomeValues } from "../../steps/hearing-aids-step/substeps/domes/dome-values.type";

export const getOrderCompletenessMessages = (stepsData: StepData) => {
  const configuration = stepsData[
    CONFIGURATION_STEP
  ] as OrderConfigurationStepValues;

  const messages: string[][] = [];
  const ear = configuration.ears;

  messages.push(getHearingAidMessages(ear, stepsData));

  if (configuration.receiversOrTubes === R_OR_T_CONFIGURATION_RECEIVERS) {
    messages.push(getReceiverMessages(ear, stepsData));
  } else if (configuration.receiversOrTubes === R_OR_T_CONFIGURATION_TUBES) {
    messages.push(getTubeMessages(ear, stepsData));
  }

  if (configuration.domesOrEarmolds === D_OR_E_CONFIGURATION_DOMES) {
    messages.push(getDomeMessages(ear, stepsData));
  } else if (configuration.domesOrEarmolds === D_OR_E_CONFIGURATION_EARMOLDS) {
    messages.push(getEarmoldMessages(ear, stepsData));
  }
  return flatten(messages).filter(Boolean);
};

const getHearingAidMessages = (ear: string, stepsData: StepData) => {
  const result: string[] = [];

  const stepId: Record<string, StepId> = {
    [EARS_CONFIGURATION_LEFT]: LEFT_HEARING_AID_SUBSTEP,
    [EARS_CONFIGURATION_RIGHT]: RIGHT_HEARING_AID_SUBSTEP,
    [EARS_CONFIGURATION_BOTH]: BINAURAL_HEARING_AID_SUBSTEP,
  };

  const values = stepsData[stepId[ear]] as EarHearingAidValues;

  if (ear === EARS_CONFIGURATION_BOTH) {
    if (!values?.left?.hearing_aid) {
      result.push("Left Hearing Aid");
    }

    if (!values?.right?.hearing_aid) {
      result.push("Right Hearing Aid");
    }
  } else if (ear === EARS_CONFIGURATION_LEFT) {
    if (!values?.left?.hearing_aid) {
      result.push("Left Hearing Aid");
    }
  } else if (ear === EARS_CONFIGURATION_RIGHT) {
    if (!values?.right?.hearing_aid) {
      result.push("Right Hearing Aid");
    }
  }
  return result;
};

const getReceiverMessages = (ear: string, stepsData: StepData) => {
  const result: string[] = [];

  const stepId: Record<string, StepId> = {
    [EARS_CONFIGURATION_LEFT]: LEFT_RECEIVER_SUBSTEP,
    [EARS_CONFIGURATION_RIGHT]: RIGHT_RECEIVER_SUBSTEP,
    [EARS_CONFIGURATION_BOTH]: BINAURAL_RECEIVER_SUBSTEP,
  };

  const values = stepsData[stepId[ear]] as EarReceiverValues;

  if (ear === EARS_CONFIGURATION_BOTH) {
    if (!values?.left?.not_needed && !values?.left?.receiver) {
      result.push("Left Receiver");
    }

    if (!values?.right?.not_needed && !values?.right?.receiver) {
      result.push("Right Receiver");
    }
  } else if (ear === EARS_CONFIGURATION_LEFT) {
    if (!values?.left?.receiver) {
      result.push("Left Receiver");
    }
  } else if (ear === EARS_CONFIGURATION_RIGHT) {
    if (!values?.right?.receiver) {
      result.push("Right Receiver");
    }
  }
  return result;
};

const getTubeMessages = (ear: string, stepsData: StepData) => {
  const result: string[] = [];

  const stepId: Record<string, StepId> = {
    [EARS_CONFIGURATION_LEFT]: LEFT_TUBE_SUBSTEP,
    [EARS_CONFIGURATION_RIGHT]: RIGHT_TUBE_SUBSTEP,
    [EARS_CONFIGURATION_BOTH]: BINAURAL_TUBE_SUBSTEP,
  };

  const values = stepsData[stepId[ear]] as EarTubeValues;

  if (ear === EARS_CONFIGURATION_BOTH) {
    if (!values?.left?.not_needed && !values?.left?.tube) {
      result.push("Left Tube");
    }

    if (!values?.right?.not_needed && !values?.right?.tube) {
      result.push("Right Tube");
    }
  } else if (ear === EARS_CONFIGURATION_LEFT) {
    if (!values?.left?.tube) {
      result.push("Left Tube");
    }
  } else if (ear === EARS_CONFIGURATION_RIGHT) {
    if (!values?.right?.tube) {
      result.push("Right Tube");
    }
  }
  return result;
};

const getDomeMessages = (ear: string, stepsData: StepData) => {
  const result: string[] = [];

  const stepId: Record<string, StepId> = {
    [EARS_CONFIGURATION_LEFT]: LEFT_DOME_SUBSTEP,
    [EARS_CONFIGURATION_RIGHT]: RIGHT_DOME_SUBSTEP,
    [EARS_CONFIGURATION_BOTH]: BINAURAL_DOME_SUBSTEP,
  };

  const values = stepsData[stepId[ear]] as EarDomeValues;

  if (ear === EARS_CONFIGURATION_BOTH) {
    if (!values?.left?.not_needed && !values?.left?.dome) {
      result.push("Left Dome");
    }

    if (!values?.right?.not_needed && !values?.right?.dome) {
      result.push("Right Dome");
    }
  } else if (ear === EARS_CONFIGURATION_LEFT) {
    if (!values?.left?.dome) {
      result.push("Left Dome");
    }
  } else if (ear === EARS_CONFIGURATION_RIGHT) {
    if (!values?.right?.dome) {
      result.push("Right Dome");
    }
  }
  return result;
};

const getEarmoldMessages = (ear: string, stepsData: StepData) => {
  const result: string[] = [];

  const stepId: Record<string, StepId> = {
    [EARS_CONFIGURATION_LEFT]: LEFT_EARMOLD_SUBSTEP,
    [EARS_CONFIGURATION_RIGHT]: RIGHT_EARMOLD_SUBSTEP,
    [EARS_CONFIGURATION_BOTH]: BINAURAL_EARMOLD_SUBSTEP,
  };

  const values = stepsData[stepId[ear]] as EarEarmoldValues;

  if (ear === EARS_CONFIGURATION_BOTH) {
    if (!values?.left?.not_needed && !values?.left?.earmold) {
      result.push("Left Earmold");
    }

    if (!values?.right?.not_needed && !values?.right?.earmold) {
      result.push("Right Earmold");
    }
  } else if (ear === EARS_CONFIGURATION_LEFT) {
    if (!values?.left?.earmold) {
      result.push("Left Earmold");
    }
  } else if (ear === EARS_CONFIGURATION_RIGHT) {
    if (!values?.right?.earmold) {
      result.push("Right Earmold");
    }
  }
  return result;
};
